import { Button } from "@/components/ui/button";
import { FictionalHome, fmt, calcMonthlyPI } from "@/hooks/useHomeBuyingSim";
import { ArrowLeft, Check } from "lucide-react";

interface Props {
  home: FictionalHome;
  loanAmount: number;
  years: number;
  onYearsChange: (y: number) => void;
  monthlyPI: number;
  totalMonthly: number;
  rate: number;
  onContinue: () => void;
  onBack: () => void;
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}

export default function MortgageChoiceScreen({ home, loanAmount, years, onYearsChange, monthlyPI, totalMonthly, rate, onContinue, onBack }: Props) {
  const options = [
    { y: 30, r: 0.065, label: '30-Year Fixed', desc: 'Lower monthly payment, more total interest over time.' },
    { y: 15, r: 0.059, label: '15-Year Fixed', desc: 'Higher monthly payment, less total interest over time.' },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Mortgage Options</h2>
        <p className="text-sm text-muted-foreground">Choose a mortgage term for the {home.home_name}.</p>
      </div>
      <div className="space-y-3">
        {options.map(o => {
          const pi = calcMonthlyPI(loanAmount, o.r, o.y);
          const total = pi + home.property_tax_monthly + home.insurance_monthly;
          const selected = years === o.y;
          return (
            <button
              key={o.y}
              onClick={() => onYearsChange(o.y)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selected ? 'glass-card glow-border ring-2 ring-primary/30' : 'glass-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-semibold text-foreground">{o.label}</span>
                {selected && <Check className="w-4 h-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground">{o.desc}</p>
              <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Rate: {(o.r * 100).toFixed(1)}%</span>
                <span className="text-muted-foreground">P&I: {fmt(pi)}/mo</span>
                <span className="font-semibold text-foreground">Total: {fmt(total)}/mo</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="glass-card p-4 rounded-xl space-y-2">
        <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider mb-2">Monthly Payment Breakdown</p>
        <Row label="Principal & Interest" value={fmt(monthlyPI)} />
        <Row label="Property Taxes" value={fmt(home.property_tax_monthly)} />
        <Row label="Insurance" value={fmt(home.insurance_monthly)} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Total Monthly Housing" value={fmt(totalMonthly)} highlight />
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1">Continue to Affordability Check</Button>
      </div>
    </div>
  );
}
